/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mask } from 'superstruct';

import IconTemplate from '~icons/carbon/template';

import { useQueryMedias } from '@adflow/frontend/feat-media';
import { ButtonCancel, ButtonSave } from '@adflow/frontend/ui-buttons';
import { EmptySection } from '@adflow/frontend/ui-common';
import { Checkbox, InputSearch } from '@adflow/frontend/ui-form';
import { LoadingIndicator } from '@adflow/frontend/ui-graphics';
import { ModalWithLayout } from '@adflow/frontend/ui-modal';
import { su, theme } from '@adflow/frontend/ui-theme';
import { useDebounceSearch } from '@adflow/frontend/util-common';
import type { ViewMediaDto } from '@adflow/shared/data-media';
import {
	DEFAULT_MEDIA_SORT,
	DEFAULT_MEDIA_SORT_DIR,
	ListMediaQueryDto,
} from '@adflow/shared/data-media';

const thumbnailScale = 0.33;

const MAX_WIDTH = 1294;

const styles = {
	thumbnailList: css({
		display: 'flex',
		flexWrap: 'wrap',
		gap: '10px',
		height: 'calc(100vh - 240px)',
		padding: '0.5rem',
	}),
	selectable: (isSelected: boolean) =>
		css({
			borderRadius: 8,
			backgroundColor: isSelected
				? theme.colors.palettes.primary.main
				: 'transparent',
			color: isSelected
				? theme.colors.palettes.primary.contrastText
				: theme.colors.text.primary,
		}),
	thumbnailImage: css({
		width: '100%',
		height: '100%',
		objectFit: 'contain',
		borderRadius: '10px',
	}),
	modalHeader: css({
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
	}),
	modalHeaderText: css(su.textMd),
	modalHeaderStyle: css({
		paddingTop: '0.75rem',
		paddingBottom: '0.75rem',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		columnGap: '8px',
	}),
	loadingContainer: css({
		width: '100vw',
		maxWidth: MAX_WIDTH,
	}),
	mediaContainer: css({
		display: 'block',
		padding: 12,
		height: 'fit-content',
		position: 'relative',
		cursor: 'pointer',
	}),
	mediaImageContainer: (width?: number | null, height?: number | null) =>
		css({
			height: (height || 100) * thumbnailScale,
			width: (width || 100) * thumbnailScale,
		}),
	mediaImage: (width?: number | null, height?: number | null) =>
		css({
			width: width || 100,
			height: height || 100,
			position: 'absolute',
			top: 12,
			backgroundColor: 'white',
			boxShadow: '0 5px 50px -6px rgba(0, 0, 0, 0.25)',
			overflow: 'hidden',
			transformOrigin: 'top left',
			transform: `scale(${thumbnailScale})`,
			contain: 'strict',
			pointerEvents: 'none',
			userSelect: 'none',
		}),
	thumbnail: css({
		backgroundPositionX: 'center',
		backgroundPositionY: 'center',
		backgroundSize: 'contain',
		backgroundRepeat: 'no-repeat',
	}),
	mediaLabel: css({
		display: 'flex',
		alignItems: 'center',
		gap: '4px',
		color: 'inherit',
		padding: '0.3rem',
		wordBreak: 'break-word',
	}),
	checkbox: css({
		justifyContent: 'center',
		color: 'inherit',
		fontSize: 16,
	}),
	bodyStyle: css({
		display: 'flex',
		flexWrap: 'wrap',
		gap: '24px',
		justifyItems: 'center',
		maxWidth: MAX_WIDTH,
	}),
	footerStyle: css({
		paddingTop: '0.75rem',
		paddingBottom: '0.75rem',
	}),
	footerGrow: css({
		flexGrow: 1,
	}),
	loadingIndicator: css({
		width: '100%',
		height: 100,
	}),
	footerButtons: css({
		display: 'flex',
		gap: '8px',
		alignItems: 'center',
	}),
};
export interface TemplateMediaModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (selectedMedia: ViewMediaDto[]) => void;
	destination: 'FACEBOOK' | 'GOOGLE' | 'GMB';
	maxSelections?: number;
	filter?: (media: ViewMediaDto) => boolean;
	selected?: ViewMediaDto[];
	disabledItems?: number[];
}

export function TemplateMediaModal({
	isOpen,
	onClose,
	onSave,
	destination,
	maxSelections,
	filter,
	selected = [],
	disabledItems = [],
}: TemplateMediaModalProps) {
	const { t } = useTranslation('facebook');

	const [search, setSearch] = useState('');
	const [draftSelected, setDraftSelected] = useState<ViewMediaDto[]>([]);
	const debouncedValue = useDebounceSearch(search);

	useEffect(() => {
		if (isOpen) {
			setDraftSelected(selected);
		}
	}, [isOpen, selected]);

	const args = mask(
		{
			sort: DEFAULT_MEDIA_SORT,
			sortDirection: DEFAULT_MEDIA_SORT_DIR,
			page: 0,
			itemsPerPage: 50,
			search: debouncedValue,
		},
		ListMediaQueryDto
	);

	const { data, isPending } = useQueryMedias(args, Boolean(50), destination);

	return (
		<ModalWithLayout
			header={
				<div css={styles.modalHeader}>
					<div css={{ margin: 0 }}>{t('selectMedia')}</div>
					<div css={styles.modalHeaderText}>
						<InputSearch
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							handleClear={() => setSearch('')}
						/>
					</div>
				</div>
			}
			headerStyle={styles.modalHeaderStyle}
			show={isOpen}
			body={
				isPending ? (
					<div css={styles.loadingContainer}>
						<LoadingIndicator />
					</div>
				) : data?.items?.length ? (
					<>
						{data?.items.map((media) => {
							const checked = draftSelected.some((m) => m.id === media.id);
							const isIncompatible = filter && !filter(media);
							const isMaxReached =
								maxSelections && draftSelected.length >= maxSelections;
							// Disable if: in disabledItems, incompatible, or (max reached AND not currently selected)
							const isDisabled =
								disabledItems.includes(media.id) ||
								(isIncompatible && !checked) ||
								(isMaxReached && !checked);

							return (
								<div
									key={media.id}
									css={[styles.mediaContainer, styles.selectable(checked)]}
									onClick={() => {
										if (isDisabled) {
											return;
										}

										setDraftSelected((currentSelection) => {
											if (currentSelection.some((m) => m.id === media.id)) {
												return currentSelection.filter(
													(m) => m.id !== media.id
												);
											}

											return [...currentSelection, media];
										});
									}}
								>
									<div
										css={styles.mediaImageContainer(media.width, media.height)}
									/>

									<div
										aria-hidden
										data-board-cover
										css={styles.mediaImage(media.width, media.height)}
									>
										<img
											key={media.id}
											alt={media.name}
											src={media.thumbnailUrl ?? media.url}
											css={styles.thumbnail}
										/>
									</div>
									<div css={styles.mediaLabel}>
										<Checkbox
											checked={checked}
											onChange={() => null}
											css={styles.checkbox}
											disabled={Boolean(isDisabled)}
										/>
										{media.name}
									</div>
								</div>
							);
						})}
						{isPending ? (
							<div css={styles.loadingIndicator}>
								<LoadingIndicator />
							</div>
						) : null}
					</>
				) : (
					<div css={styles.loadingContainer}>
						<EmptySection
							Icon={IconTemplate}
							iconStyle={{ height: 'unset' }}
							message={t('noResults')}
						/>
					</div>
				)
			}
			bodyStyle={styles.bodyStyle}
			footer={
				<>
					<div css={styles.footerGrow} />
					<ButtonCancel
						onClick={() => {
							onClose();
						}}
					>
						Cancel
					</ButtonCancel>
					<ButtonSave
						onClick={() => {
							onSave(draftSelected);
							onClose();
						}}
					>
						Done
					</ButtonSave>
				</>
			}
			footerStyle={styles.footerStyle}
		/>
	);
}
